import {Date as ApiDate, FullDate as ApiFullDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {AggregatedSignalDataBlock} from 'gabi-api-ts/v2/signal/query/signal_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import {useTranslation} from 'react-i18next';
import {Line, LineChart, Tooltip, XAxis, YAxis} from 'recharts';
import {CategoricalChartFunc} from 'recharts/types/chart/generateCategoricalChart';
import {CategoricalChartState} from 'recharts/types/chart/types';
import {TooltipProps} from 'recharts/types/component/Tooltip';
import styled from 'styled-components';

import {ChartAxisSecondsSince} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-seconds-since';
import {ChartAxisTickColored} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-colored';
import {getDomainForSignalType, getXDomainFromTimeInterval} from '@/components/business/analytics/timeline/timeline-chart-domains';
import {ChartContainer} from '@/components/layout/chart-container';
import LoadingView from '@/components/static/loading-view';
import ComponentErrorMessage from '@/components/widgets/component-error-message';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {colorPalette} from '@/themes/darkmode';
import {addSeconds, defaultApiTime, formatApiDateTimeUniversal, getDaysDifference, getSecondsDifference} from '@/util/apidate-util';
import {roundTo} from '@/util/number-util';
import {getSignalTypeLabel, getSignalTypeUnit} from '@/util/signal-type-util';

type HealthReportAggregatedSignalProps = {
    dateFrom: ApiDate;
    dateTo: ApiDate;
    maxX?: number;
    minX?: number;
    patientId: string;
    signalType: SignalType;
    onClick?: (data: FormattedAggregatedSignalDataBlock) => void;
};

export type FormattedAggregatedSignalDataBlock = AggregatedSignalDataBlock & {
    fullDate: ApiFullDate;
    seconds: number;
    value: number;
}

function HealthReportSpotSignal({dateFrom, dateTo, patientId, signalType, minX, maxX, onClick}: HealthReportAggregatedSignalProps) {
    const { t } = useTranslation();

    const [ biometricHistoryLoading, biometricHistory, /*eventsListError*/ ] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getBiometricHistory,
        memoize: false,
        data: {
            patientId: {id: patientId},
            from: dateFrom,
            to: dateTo,
            signalType: [
                signalType,
            ],
        },
    }, []);

    const title = getSignalTypeLabel(signalType);

    const loading = (
        (biometricHistoryLoading || !biometricHistory)
    );

    minX ??= getDaysDifference(dateFrom, dateTo) * 24 * 60 * 60 + 1;
    maxX ??= 0;
    const biometricChartData = biometricHistory?.entries
        .filter(point => point.values.findIndex(v => v.quality > 0) >= 0)
        .map(point => {
            const fullDate = point.timestamps;
            const value = point.values[0].value;
            const seconds = getSecondsDifference({
                date: dateFrom,
                time: {hour: 0, minutes: 0, seconds: 0, milliSeconds: 0, offset: 0},
            }, fullDate!);
            minX = Math.min(minX!, seconds);
            maxX = Math.max(maxX!, seconds);
            return {
                fullDate: fullDate,
                value: value,
                seconds: seconds,
            };
        });

    const signalColors = (SignalDefinitions[signalType] ?? SignalDefinitions[-1]).colors;

    if (loading) {
        return (
            <StyledWrapper className="health-report-aggregated-signal" color={signalColors.light}>
                <ChartContainer title={title}>
                    <LoadingView color={signalColors.normal} />
                </ChartContainer>
            </StyledWrapper>
        );
    }

    const signalRange = [0.0, 100.0/*aggregatedSignal.valueMin, aggregatedSignal.valueMax*/];

    const fullDateFrom = addSeconds({date: dateFrom, time: defaultApiTime}, minX);
    fullDateFrom.time.minutes = fullDateFrom.time.minutes < 30 ? 0 : 30;
    fullDateFrom.time.seconds = 0;
    fullDateFrom.time.milliSeconds = 0;
    const fullDateTo   = addSeconds({date: dateFrom, time: defaultApiTime}, maxX);

    const renderTooltip = (data: TooltipProps<number | string | Array<number | string>, string | number>) => {
        if (data?.payload) {
            const payload = data?.payload[0]?.payload;

            if (payload) {
                const value = payload.value;
                return (
                    <div className="chart-tooltip">
                        <span className="tooltip-value">
                            {getSignalTypeLabel(signalType)}: {roundTo(value, 2)}{getSignalTypeUnit(signalType)}
                        </span>
                        <span className="tooltip-time">{formatApiDateTimeUniversal(payload.fullDate.date, payload.fullDate.time)}</span>
                    </div>
                );
            }
        }
        return null;
    };

    const domainDefinition = getDomainForSignalType(signalType, signalRange[0], signalRange[1]);

    if (patientId) {
        const topRightContent = <span></span>;
        const xAxisDomain = getXDomainFromTimeInterval(fullDateFrom, fullDateTo);

        const handleClick: CategoricalChartFunc = (data: CategoricalChartState) => {
            if (onClick) {
                if (data.activePayload && data.activePayload[0] && data.activePayload[0].payload) {
                    onClick(data.activePayload[0].payload as FormattedAggregatedSignalDataBlock);
                }
            }
        };

        return (
            <StyledWrapper className="health-report-aggregated-signal" color={signalColors.light}>
                <ChartContainer
                    title={title}
                    topRightContent={topRightContent}
                    infoTooltipTitle={`${getSignalTypeLabel(signalType)} - ${t('infoButton.aggregatedSignal.title') ?? 'aggregated signal'}`}
                    infoTooltipOverlayPosition="bottomRight"
                >
                    <LineChart data={biometricChartData} margin={{ top: 0, right: 0, bottom: 0, left: 10 }} onClick={handleClick}>
                        <XAxis
                            type="number"
                            dataKey="seconds"
                            tickCount={6}
                            domain={[minX, maxX]}
                            ticks={xAxisDomain.ticks}
                            tick={<ChartAxisSecondsSince since={{date: dateFrom, time: {hour: 0, minutes: 0, seconds: 0, milliSeconds: 0, offset: 0}}} textAnchor="middle" />}
                            padding={{ right: 20 }}
                            height={37}
                        />
                        <YAxis
                            dataKey="value"
                            tick={(props) => <ChartAxisTickColored {...props} unit={getSignalTypeUnit(signalType)} fill={signalColors.light} />}
                            domain={domainDefinition.domain}
                            ticks={domainDefinition.ticks}
                            interval={0}
                            width={56}
                            padding={{ top: 10, bottom: 10 }}
                        />
                        <Line
                            type="monotone"
                            dot={true}
                            dataKey="value"
                            stroke={signalColors.normal}
                            strokeWidth={2.0}
                        />
                        <Tooltip
                            content={renderTooltip}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ChartContainer>
            </StyledWrapper>
        );
    }
    else {
        return <ComponentErrorMessage component="HealthReportAggregatedSignal" />;
    }
}

//language=SCSS
const StyledWrapper = styled.div`
& {
    min-height: 260px;
    height: 100%;
    
    .chart-container {
        min-height: inherit;
    }
    
    .tooltip-time {
        color: ${colorPalette.frontColor};
    }
    
    .tooltip-value {
        color: ${props => props.color};
    }
    
    .tooltip-no-data {
        color: ${colorPalette.clearColor};
    }
    
    .tooltip-not-reliable {
        color: ${colorPalette.clearColor};
    }

    svg {
        cursor: pointer;
    }
    
    .average-block {
        p span {
            color: ${props => props.color};
        }

        img.warningFlag {
            width: 15px;
            margin-right: 7px;
            margin-top: -3px;
        }
    }
}
`;

export {HealthReportSpotSignal};
